import { Button } from '@shared/components/ui/button';
import { Checkbox } from '@shared/components/ui/checkbox';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Separator } from '@shared/components/ui/shadcn/separator';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIdentifyPackage } from '../../hooks/useIdentifyPackage';
import { AdvancedEntrySelect } from '../ui/AdvancedEntrySelect';
import { DESTINATION_TYPE_OPTIONS, DestinationType, IdentifyPayload, Reception } from '../../models/Reception';

interface IdentifyPackagesModalProps {
  reception: Reception;
  onComplete?: () => void;
}

interface PayloadState {
  entry_id?: number;
  total_orders?: number;
  consumable?: string;
}

const IdentifyPackagesModal = forwardRef<ModalCenterRef, IdentifyPackagesModalProps>(
  ({ reception, onComplete }, ref) => {
    const { t } = useTranslation('receptionsModule');
    const { toast } = useToast();
    const identifyMutation = useIdentifyPackage();

    // Paquetes pendientes (RECEIVED)
    const pendingPackages = reception.packages?.filter((p) => p && p.status === 'RECEIVED') || [];
    const identifiedPackages = reception.packages?.filter((p) => p && p.status === 'IDENTIFIED') || [];
    const totalPackages = reception.packages?.length || 0;

    // Estado de selección
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    // Estado del formulario de identificación
    const [destinationType, setDestinationType] = useState<DestinationType | ''>('');
    const [payload, setPayload] = useState<PayloadState>({});
    const [selectedEntryId, setSelectedEntryId] = useState<string>('');

    // Reset al abrir
    useEffect(() => {
      setSelectedIds(new Set());
      setDestinationType('');
      setPayload({});
      setSelectedEntryId('');
    }, [reception.id]);

    // Sincronizar selectedEntryId con payload
    useEffect(() => {
      if (selectedEntryId) {
        setPayload((prev) => ({
          ...prev,
          entry_id: parseInt(selectedEntryId) || 0,
        }));
      }
    }, [selectedEntryId]);

    const handleClose = () => {
      (ref as React.RefObject<ModalCenterRef>)?.current?.closeModal();
    };

    const handleSelectPackage = (pkgId: number, checked: boolean) => {
      const newSelected = new Set(selectedIds);
      if (checked) {
        newSelected.add(pkgId);
      } else {
        newSelected.delete(pkgId);
      }
      setSelectedIds(newSelected);
    };

    const handleSelectAll = () => {
      if (selectedIds.size === pendingPackages.length) {
        setSelectedIds(new Set());
      } else {
        setSelectedIds(new Set(pendingPackages.map((p) => p.id)));
      }
    };

    const handleDestinationChange = (value: DestinationType) => {
      setDestinationType(value);
      setPayload({});
      setSelectedEntryId('');
    };

    const getPayloadForType = (): IdentifyPayload => {
      switch (destinationType) {
        case 'INBOUND':
          return { entry_id: payload.entry_id || 0 };
        case 'REJECTED':
        case 'RETURN':
          return { total_orders: payload.total_orders || 0 };
        case 'SUPPLY':
          return { consumable: payload.consumable || '' };
        default:
          return { entry_id: 0 }; // Fallback a INBOUND
      }
    };

    const isPayloadValid = (): boolean => {
      switch (destinationType) {
        case 'INBOUND':
          return (payload.entry_id || 0) > 0;
        case 'REJECTED':
        case 'RETURN':
          return (payload.total_orders || 0) > 0;
        case 'SUPPLY':
          return !!payload.consumable?.trim();
        default:
          return false;
      }
    };

    const handleIdentify = async () => {
      if (selectedIds.size === 0 || !destinationType || !isPayloadValid()) return;

      const selectedPackages = pendingPackages.filter((p) => selectedIds.has(p.id));
      const payloadData = getPayloadForType();

      try {
        // Identificar cada paquete seleccionado
        for (const pkg of selectedPackages) {
          await identifyMutation.mutateAsync({
            receptionId: reception.id,
            pkg,
            receptionStatus: reception.status,
            data: {
              destination_type: destinationType,
              payload: payloadData,
            },
          });
        }

        toast({
          title: t('modal.identify.success_title'),
          description: t('modal.identify.success_description_bulk', {
            count: selectedPackages.length,
          }),
        });

        // Reset selección
        setSelectedIds(new Set());
        setDestinationType('');
        setPayload({});
        setSelectedEntryId('');

        // Si todos están identificados, cerrar y notificar
        if (selectedPackages.length === pendingPackages.length) {
          handleClose();
          onComplete?.();
        }
      } catch (error: any) {
        console.error(error);
        toast({
          title: t('modal.identify.error_title'),
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      }
    };

    const isLoading = identifyMutation.isPending;
    const canIdentify = selectedIds.size > 0 && destinationType && isPayloadValid();
    const progress = totalPackages > 0 ? Math.round((identifiedPackages.length / totalPackages) * 100) : 0;

    return (
      <ModalCenter
        ref={ref}
        title={t('modal.identify.title')}
        type="default"
        width="xl"
        classNameBody="max-h-[85vh] flex flex-col"
      >
        <div className="flex-1 overflow-y-auto pr-2">
          <div className="space-y-6">
            {/* Barra de progreso */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-complementary-text">{t('modal.identify.progress_label')}</span>
                <span className="font-medium">
                  {identifiedPackages.length} / {totalPackages}
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-success transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            </div>

            <Separator />

            {pendingPackages.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-success font-medium mb-2">{t('modal.identify.all_identified')}</p>
                <Button variant="outline" onClick={handleClose}>
                  {t('button.close')}
                </Button>
              </div>
            ) : (
              <>
                {/* Lista de paquetes pendientes */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium">
                      {t('modal.identify.pending_packages')} ({pendingPackages.length})
                    </h3>
                    <button onClick={handleSelectAll} className="text-xs text-primary hover:underline">
                      {selectedIds.size === pendingPackages.length
                        ? t('modal.identify.deselect_all')
                        : t('modal.identify.select_all')}
                    </button>
                  </div>

                  <div className="border rounded-lg divide-y divide-border max-h-[200px] overflow-auto">
                    {pendingPackages.map((pkg) => {
                      const isSelected = selectedIds.has(pkg.id);
                      const typeLabel =
                        pkg.package_type === 'OTHER' && pkg.package_type_other
                          ? pkg.package_type_other
                          : t(`package_types.${pkg.package_type.toLowerCase()}`);

                      return (
                        <label
                          key={pkg.id}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors ${
                            isSelected ? 'bg-primary/5' : ''
                          }`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => handleSelectPackage(pkg.id, !!checked)}
                            disabled={isLoading}
                          />
                          <div className="flex-1 flex items-center justify-between">
                            <span className="text-sm font-medium">{typeLabel}</span>
                            <span className="text-sm text-complementary-text">{pkg.quantity} uds</span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <Separator />

                {/* Formulario de identificación */}
                <div className={selectedIds.size === 0 ? 'opacity-50 pointer-events-none' : ''}>
                  <h3 className="text-sm font-medium mb-3">
                    {t('modal.identify.destination_for', {
                      count: selectedIds.size,
                    })}
                  </h3>

                  <div className="space-y-4">
                    {/* Tipo de destino */}
                    <div>
                      <label className="text-xs text-complementary-text mb-1 block">
                        {t('modal.identify.destination_type_label')}
                      </label>
                      <Select
                        value={destinationType}
                        onValueChange={(value) => handleDestinationChange(value as DestinationType)}
                        disabled={isLoading || selectedIds.size === 0}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('modal.identify.destination_type_placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {DESTINATION_TYPE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {t(option.labelKey)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Campos según tipo de destino */}
                    {destinationType === 'INBOUND' && (
                      <div>
                        <label className="text-xs text-complementary-text mb-1 block">
                          {t('modal.identify.entry_label')}
                        </label>
                        <AdvancedEntrySelect
                          value={selectedEntryId}
                          onValueChange={setSelectedEntryId}
                          placeholder={t('modal.identify.entry_placeholder')}
                          disabled={isLoading}
                          dataTestId="entry-select"
                        />
                      </div>
                    )}

                    {(destinationType === 'REJECTED' || destinationType === 'RETURN') && (
                      <div>
                        <label className="text-xs text-complementary-text mb-1 block">
                          {t('modal.identify.total_orders_label')}
                        </label>
                        <Input
                          type="number"
                          min={1}
                          placeholder={t('modal.identify.total_orders_placeholder')}
                          value={payload.total_orders || ''}
                          onChange={(e) =>
                            setPayload({
                              ...payload,
                              total_orders: parseInt(e.target.value) || 0,
                            })
                          }
                          disabled={isLoading}
                        />
                      </div>
                    )}

                    {destinationType === 'SUPPLY' && (
                      <div>
                        <label className="text-xs text-complementary-text mb-1 block">
                          {t('modal.identify.consumable_label')}
                        </label>
                        <Input
                          placeholder={t('modal.identify.consumable_placeholder')}
                          value={payload.consumable || ''}
                          onChange={(e) => setPayload({ ...payload, consumable: e.target.value })}
                          disabled={isLoading}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <Separator />
              </>
            )}
          </div>
        </div>

        {/* Footer fijo siempre visible */}
        {pendingPackages.length > 0 && (
          <div className="flex-shrink-0 pt-4 mt-4 border-t border-card-border">
            <div className="flex justify-between items-center">
              <span className="text-xs text-complementary-text">
                {selectedIds.size > 0
                  ? t('modal.identify.packages_selected', {
                      count: selectedIds.size,
                    })
                  : t('modal.identify.select_packages_hint')}
              </span>
              <div className="flex gap-3">
                <Button variant="outline" onClick={handleClose} disabled={isLoading}>
                  {t('button.cancel')}
                </Button>
                <Button variant="primary" onClick={handleIdentify} disabled={!canIdentify || isLoading}>
                  {isLoading
                    ? t('button.identifying')
                    : t('modal.identify.identify_button_count', {
                        count: selectedIds.size,
                      })}
                </Button>
              </div>
            </div>
          </div>
        )}
      </ModalCenter>
    );
  },
);

IdentifyPackagesModal.displayName = 'IdentifyPackagesModal';

export default IdentifyPackagesModal;
